import { Injectable } from '@angular/core';

import { combineLatest, Observable } from 'rxjs';
import { map, withLatestFrom } from 'rxjs/operators';

import { Role, Tutorial, TutorialDto, TutorialType } from 'app/core/models';
import { AngularFirestoreCollection, AuthenticationService, FirestoreService, RolesService, TutorialsService } from 'app/core/services';

@Injectable()
export class FirestoreTutorialsService implements TutorialsService {
    private get tutorialsCollection(): AngularFirestoreCollection<TutorialDto> {
        return this.firestoreService.tenantDependentCollection<TutorialDto>(this.authenticationService.user.tenantId, 'tutorials');
    }

    constructor(
        private authenticationService: AuthenticationService,
        private firestoreService: FirestoreService,
        private rolesService: RolesService,
    ) {}

    /** fetch the currently available tutorials */
    tutorials(): Observable<Tutorial[]> {
        const tutorialsObservable = this.tutorialsCollection.snapshotChanges().pipe(
            map((result) => {
                return result.map((tutorialDoc) => {
                    const tutorial = tutorialDoc.payload.doc.data();
                    tutorial.id = tutorialDoc.payload.doc.id;
                    return tutorial;
                });
            })
        );

        return tutorialsObservable.pipe(
            withLatestFrom(combineLatest([this.rolesService.roles()])),
            map(([tutorialsDTOs, [roles]]: [TutorialDto[], [Role[]]]) => {
                return tutorialsDTOs.map((tutorialDto) => {
                    return this.tutorialMapper(tutorialDto, roles);
                });
            })
        );
    }

    private tutorialMapper(tutorialDto: TutorialDto, roles: Role[]): Tutorial {
        return {
            availableTo: tutorialDto.availableTo
                ? tutorialDto.availableTo.map((roleId: string) => roles.find((role: Role) => role.id === roleId))
                : [],
            createdAt: tutorialDto.createdAt && tutorialDto.createdAt.toDate(),
            id: tutorialDto.id,
            name: tutorialDto.name,
            sectionsIds: tutorialDto.sectionsIds ?? [],
            source: tutorialDto.source,
            type: tutorialDto.type && ['video'].indexOf(tutorialDto.type) >= 0
                ? (tutorialDto.type as TutorialType)
                : 'video',
        };
    }
}
